/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Header wishlist (heart) icon visibility.
 *
 * The header heart is hidden by default and only appears once the shopper has added
 * something with a product heart (tile, PDP, cart, or a post-login pending add).
 * The flag is a first-party cookie so it survives reloads and navigation for guests
 * and registered shoppers alike; a same-tab event lets the header react immediately.
 */
export const WISHLIST_ICON_COOKIE_NAME = 'wishlist_icon_visible';

const WISHLIST_ICON_EVENT = 'wishlist-icon-visible';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

export function isWishlistIconVisible(): boolean {
    if (typeof document === 'undefined') return false;
    return document.cookie.split(';').some((c) => c.trim() === `${WISHLIST_ICON_COOKIE_NAME}=1`);
}

/** Flag the header heart as visible. Safe to call repeatedly and on the server (no-op). */
export function markWishlistIconVisible(): void {
    if (typeof document === 'undefined' || isWishlistIconVisible()) return;
    document.cookie = `${WISHLIST_ICON_COOKIE_NAME}=1; Max-Age=${ONE_YEAR_SECONDS}; Path=/; SameSite=Lax`;
    window.dispatchEvent(new Event(WISHLIST_ICON_EVENT));
}

/** `useSyncExternalStore` subscriber for {@link isWishlistIconVisible}. */
export function subscribeWishlistIconVisibility(listener: () => void): () => void {
    window.addEventListener(WISHLIST_ICON_EVENT, listener);
    return () => {
        window.removeEventListener(WISHLIST_ICON_EVENT, listener);
    };
}
